import React, { useState, useRef, useEffect } from "react";
import ViewOffIcon from "../../images/ViewOffIcon";
import PlusIcon from "../../images/PlusIcon";
import ViewOnIcon from "../../images/ViewOnIcon";
import {
  webLoadingFalse,
  webNavigate,
} from "../../services/navigate.service";
import CloseIcon from "../../images/CloseIcon";
import { deleteSales, getSales } from "../../services/sales.service";
import { useResponsive } from "../../hook/useResponsive";
import SalesIcon from "../../images/SalesIcon";
import EditIconV2 from "../../images/EditIconV2";
import DeleteIconV2 from "../../images/DeleteIconV2";
import TagStatus from "../../components/TagStatus";
import { ConfirmSheet } from "../../components/mobile";
import { showApiError } from "../../services/apiError";
import {
  ListColumn,
  ListQuery,
  ListRowAction,
  ListScreen,
  ListScreenHandle,
  cells,
  formatMoney,
} from "../../components/list";

interface SaleRow {
  id: string;
  invoiceNumber?: string | null;
  dueDate: string;
  reference?: string | null;
  amount: string;
  amountPaid?: string | null;
  client?: { companyName?: string } | null;
  term?: { termName?: string } | null;
  status?: { statusName?: string } | null;
  saleDetail?: { amount: string }[];
}

/** Format a due date as dd.mm.yyyy (consistent with the TAK Libri exports). */
const fmtDueDate = (value: string): string => {
  if (!value) return "";
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return value;
  const dd = String(d.getUTCDate()).padStart(2, "0");
  const mm = String(d.getUTCMonth() + 1).padStart(2, "0");
  return `${dd}.${mm}.${d.getUTCFullYear()}`;
};

/**
 * Days an invoice is past due, for the table's "Days Overdue" column. Only
 * returns a value when the invoice is still owed (Balance = amountPaid - total
 * is negative) and the due date has passed; otherwise blank. Read-only display;
 * does not touch status values or business logic.
 */
const daysOverdueOf = (dueDate: string, balance: number): string => {
  if (!dueDate || balance >= -0.005) return "";
  const due = new Date(dueDate);
  if (Number.isNaN(due.getTime())) return "";
  const diff = Math.floor((Date.now() - due.getTime()) / 86400000);
  return diff > 0 ? String(diff) : "";
};

const totalOf = (row: SaleRow): number =>
  (row.saleDetail ?? []).reduce((acc: number, val: any) => acc + Number(val.amount), 0);
// Balance = amountPaid - total (unchanged): negative = still owed,
// positive = overpaid/credit.
const balanceOf = (row: SaleRow): number => Number(row.amountPaid) - totalOf(row);
const statusOf = (row: SaleRow): string => row.status?.statusName ?? "";

const Sales = () => {
  const preLanguage = localStorage.getItem("language");
  const [hiddenSale, setHiddenSale] = useState<boolean>(false);
  const [language, setLanguage] = useState<any>(null);
  const { isTouchLayout, isPhone } = useResponsive();
  const [listData, setListData] = useState<SaleRow[]>([]);
  const [showDelete, setShowDelete] = useState<boolean>(false);
  const [idDelete, setIdDelete] = useState<string>("");
  // Invoice number of the row queued for deletion, so the confirmation names
  // exactly which sale is about to be permanently removed.
  const [invoiceDelete, setInvoiceDelete] = useState<string>("");
  const searchInputRef = useRef<HTMLInputElement | null>(null);
  const listRef = useRef<ListScreenHandle>(null);
  const inforTable: any = {
    img: <SalesIcon />,
    title:
      language && language?.["No sales yet"]
        ? language?.["No sales yet"]
        : "No sales yet",
    path: "/sales/new",
    desc:
      language && language?.["Looks like you haven’t made any sales yet."]
        ? language?.["Looks like you haven’t made any sales yet."]
        : "Looks like you haven’t made any sales yet.",
  };

  const handleKeyDown = (event: KeyboardEvent) => {
    if ((event.ctrlKey || event.metaKey) && event.key === "f" && searchInputRef.current) {
      event.preventDefault();
      searchInputRef.current.focus();
      searchInputRef.current.select();
    }
  };
  useEffect(() => {
    if (window.languageContext) {
      setLanguage(window.languageContext.language);
    }
  }, [window, window.languageContext]);
  useEffect(() => {
    window.addEventListener("keydown", handleKeyDown);
    return () => {
      window.removeEventListener("keydown", handleKeyDown);
    };
  }, []);

  /** One page of sales; the search box maps onto `searchValue` (invoice number or reference). */
  const fetchSales = async (query: ListQuery) => {
    webLoadingFalse();
    const response: any = await getSales(query.page, query.limit, query.search.trim());
    const rows: SaleRow[] = response?.data?.salesWithStatus ?? [];
    return { rows, count: response?.data?.count ?? 0 };
  };

  const goToSale = (id: string) => webNavigate("/" + preLanguage + "/sales/" + id);

  const handleShowDelete = (row: SaleRow) => {
    setInvoiceDelete(row.invoiceNumber ?? "");
    setShowDelete(true);
    setIdDelete(row.id);
  };
  const handleDeleteSale = async () => {
    try {
      await deleteSales(idDelete);
    } catch (error) {
      console.error("Error deleting sale:", error);
      showApiError(error, { language });
    } finally {
      listRef.current?.reload();
      setShowDelete(false);
    }
  };

  /* ---------------- List kit configuration ---------------- */

  const columns: ListColumn<SaleRow>[] = [
    {
      key: "invoiceNumber",
      label: "Invoice",
      width: 140,
      minWidth: 100,
      hideable: false,
      value: r => r.invoiceNumber ?? "/",
      render: cells.primary(r => r.invoiceNumber ?? "/"),
      mobile: "title",
      onClick: r => goToSale(String(r.id)),
    },
    { key: "client", label: "Client", width: 200, minWidth: 120, value: r => r.client?.companyName ?? "", mobile: "subtitle" },
    { key: "dueDate", label: "Due Date", width: 110, minWidth: 96, value: r => fmtDueDate(r.dueDate), mobile: "subtitle" },
    {
      key: "daysOverdue",
      label: "Days Overdue",
      width: 110,
      minWidth: 84,
      align: "right",
      value: r => daysOverdueOf(r.dueDate, balanceOf(r)),
      mobile: "sheet",
    },
    { key: "term", label: "Payment Term", width: 140, minWidth: 96, value: r => r.term?.termName ?? "", mobile: "sheet" },
    { key: "reference", label: "Reference", width: 150, minWidth: 96, value: r => r.reference ?? "", mobile: "sheet" },
    {
      key: "balance",
      label: "Balance",
      width: 110,
      minWidth: 88,
      align: "right",
      value: r => formatMoney(balanceOf(r)),
      render: cells.money(r => balanceOf(r)),
      mobile: "sheet",
    },
    {
      key: "total",
      label: "Total",
      width: 110,
      minWidth: 88,
      align: "right",
      value: r => formatMoney(totalOf(r)),
      render: cells.money(r => totalOf(r), true),
      mobile: "trailing",
    },
    {
      key: "status",
      label: "Status",
      width: 130,
      minWidth: 110,
      value: r => statusOf(r),
      render: (r, ctx) => <TagStatus value={statusOf(r)} label={ctx.language?.[statusOf(r)]} />,
      mobile: "trailing",
    },
  ];

  const rowActions: ListRowAction<SaleRow>[] = [
    {
      key: "edit",
      label: "Edit",
      icon: <EditIconV2 />,
      sheetIcon: "sales",
      onRun: r => goToSale(String(r.id)),
    },
    {
      key: "delete",
      label: "Delete",
      icon: <DeleteIconV2 />,
      sheetIcon: "close",
      danger: true,
      onRun: r => handleShowDelete(r),
    },
  ];

  /** KPI tile figure: the current page's rows only, summed on the header `amount`. */
  const sumAmount = (statuses?: string[]) =>
    formatMoney(
      listData
        .filter((val: SaleRow) => !statuses || statuses.includes(statusOf(val)))
        .reduce((acc: number, val: SaleRow) => acc + Number(val.amount), 0),
    );
  const kpiTileStyle: React.CSSProperties = {
    width: isTouchLayout ? "calc(50% - 12px)" : "calc(24% - 12px)",
    minWidth: 0,
    border: "1px solid #EBF0F0",
    padding: "16px",
    borderRadius: "12px",
  };
  const kpiValueStyle: React.CSSProperties = {
    color: "#222525",
    fontWeight: "600",
    fontSize: isPhone ? "18px" : "24px",
    fontVariantNumeric: "tabular-nums",
    overflowWrap: "anywhere",
    minWidth: 0,
  };
  const kpiTile = (labelKey: string, value: string) => (
    <div className="space-y-2" style={kpiTileStyle}>
      <div className="lg:flex items-center justify-between">
        <p
          className="text-[14px] font-inter"
          style={{ color: "#4A5353" }}
        >
          {language && language?.[labelKey] ? language?.[labelKey] : labelKey}
        </p>
      </div>
      <div className="flex items-center justify-between">
        <p className="font-inter" style={kpiValueStyle}>
          {value}
        </p>
      </div>
    </div>
  );

  const deleteTitle =
    language && language?.["Delete Sales?"]
      ? language?.["Delete Sales?"]
      : "Delete Sales";
  const deleteMessage = (
    <>
      {language && language?.["You will permanently delete the sale"]
        ? language?.["You will permanently delete the sale"]
        : "You will permanently delete the sale"}
      {invoiceDelete ? (
        <span style={{ color: "#222525", fontWeight: 600 }}>
          {" "}
          {invoiceDelete}
        </span>
      ) : null}
      .
    </>
  );
  const cancelLabel =
    language && language?.["Cancel"] ? language?.["Cancel"] : "Cancel";
  const deleteLabel =
    language && language?.["Delete"] ? language?.["Delete"] : "Delete";

  return (
    <>
      <div
        style={{
          position: "relative",
          height: "100%",
          display: "flex",
          flexDirection: "column",
          minHeight: 0,
          overflowY: isTouchLayout ? "auto" : undefined,
          paddingBottom: isTouchLayout ? "24px" : undefined,
        }}
      >
        <div className="lg:flex items-center justify-between">
          <p
            style={{ color: "#222525" }}
            className="text-[24px] leading-[130%] font-medium font-inter"
          >
            {language && language?.["Sales"] ? language?.["Sales"] : "Sales"}
          </p>
          <div
            className="flex items-center"
            style={{
              gap: "16px",
              marginTop: isTouchLayout ? "20px" : 0,
              flexWrap: "wrap",
            }}
          >
            <div className="flex items-center gap-x-2">
              <div
                style={{
                  padding: "10px 24px",
                  border: "1px solid #6A7878",
                  borderRadius: "40px",
                }}
                className={`flex items-center justify-center cursor-pointer transition-all duration-300 ease-in-out `}
              >
                {!hiddenSale ? (
                  <div
                    className="w-full flex items-center gap-x-2 cursor-pointer"
                    onClick={() => {
                      setHiddenSale(!hiddenSale);
                    }}
                  >
                    <ViewOffIcon />
                    <p
                      className="text-[14px] leading-[20px] font-inter"
                      style={{ color: "#6A7878", fontWeight: "500" }}
                    >
                      {language && language?.["Hide Metrics"]
                        ? language?.["Hide Metrics"]
                        : "Hide Metrics"}
                    </p>
                  </div>
                ) : (
                  <div
                    className="w-full flex items-center gap-x-2 cursor-pointer"
                    onClick={() => {
                      setHiddenSale(!hiddenSale);
                    }}
                  >
                    <ViewOnIcon />
                    <p
                      className="text-[14px] leading-[20px] font-inter"
                      style={{ color: "#6A7878", fontWeight: "500" }}
                    >
                      {language && language?.["Show Metrics"]
                        ? language?.["Show Metrics"]
                        : "Show Metrics"}
                    </p>
                  </div>
                )}
              </div>
            </div>
            <div
              className="cursor-pointer flex items-center justify-center"
              style={{
                gap: "8px",
                padding: "8px 24px 8px 16px",
                backgroundColor: "#0F7679",
                borderRadius: "30px",
              }}
              onClick={() => {
                webNavigate("/" + preLanguage + "/sales/new");
              }}
            >
              <div style={{ width: '24px', height: '24px' }} className="flex items-center justify-center">
                <PlusIcon />
              </div>
              <p
                style={{ color: "#FFFFFF" }}
                className="text-[14px] leading-[130%] font-medium font-inter"
              >
                {language && language?.["Add sale"]
                  ? language?.["Add sale"]
                  : "Add sale"}
              </p>
            </div>
          </div>
        </div>
        {!hiddenSale && (
          <div
            className={`items-center justify-between transition-all duration-1000 ease-in-out flex`}
            style={{ marginTop: "24px", gap: "12px", flexWrap: "wrap" }}
          >
            <p
              className="text-[12px] font-inter"
              style={{ width: "100%", margin: 0, color: "#4A5353" }}
            >
              {language &&
              language?.["Figures reflect the invoices currently listed"]
                ? language?.["Figures reflect the invoices currently listed"]
                : "Figures reflect the invoices currently listed"}
            </p>
            {kpiTile("Total Sales", sumAmount())}
            {kpiTile("Outstanding Invoices", sumAmount(["Overdue", "Partially Paid", "Sent"]))}
            {kpiTile("Delayed Payments", sumAmount(["Overdue"]))}
            {kpiTile("Canceled Payments", sumAmount(["Rejected", "Cancelled"]))}
          </div>
        )}
        <ListScreen
          ref={listRef}
          language={language}
          columns={columns}
          fetchRows={fetchSales}
          onLoaded={(result: any) => setListData(result?.rows ?? [])}
          searchPlaceholder="Search invoice or reference"
          searchInputRef={searchInputRef}
          rowActions={rowActions}
          countNoun="sales"
          emptyState={inforTable}
        />
      </div>
      {isTouchLayout ? (
        <ConfirmSheet
          open={showDelete}
          title={deleteTitle}
          message={deleteMessage}
          confirmLabel={deleteLabel}
          cancelLabel={cancelLabel}
          onConfirm={handleDeleteSale}
          onCancel={() => setShowDelete(false)}
        />
      ) : (
        <div
          className={`absolute w-full h-full top-0 left-0 flex items-center justify-center px-4 ${showDelete ? "opacity-100 pointer-events-auto" : "opacity-0 pointer-events-none"} transition-opacity duration-300 ease-in-out`}
          style={{
            backgroundColor: "#22252552",
            zIndex: "200",
            transition: "opacity 0.3s ease, visibility 0.3s ease",
            width: "100vw",
            height: "100vh",
            left: 0,
            top: 0,
          }}
        >
          <div
            className={`transform transition-transform duration-300 ease-in-out ${showDelete ? "translate-y-0" : "translate-y-4 opacity-0"}`}
            style={{
              padding: "24px",
              borderRadius: "24px",
              backgroundColor: "#FFFFFF",
              width: "530px",
              transition: "transform 0.3s ease, opacity 0.3s ease",
            }}
          >
            <div className="flex items-center justify-between">
              <p
                style={{ fontSize: "20px", color: "#222525" }}
                className="font-semibold font-inter leading-[24px]"
              >
                {deleteTitle}
              </p>
              <div
                onClick={() => setShowDelete(false)}
                className="flex items-center justify-center cursor-pointer"
                style={{
                  width: "24px",
                  height: "24px",
                }}
              >
                <CloseIcon />
              </div>
            </div>
            <div className="flex items-center justify-between">
              <p
                className="text-[14px] font-inter leading-[24px]"
                style={{ color: "#6A7878" }}
              >
                {deleteMessage}
              </p>
            </div>
            <div
              className="flex items-center justify-end"
              style={{ marginTop: "24px", gap: "12px" }}
            >
              <div
                onClick={() => setShowDelete(false)}
                className="cursor-pointer text-[14px] leading-[22px] font-inter font-medium"
                style={{
                  borderRadius: "30px",
                  padding: "10px 24px",
                  border: "1px solid #C9D7D8",
                  color: "#222525",
                }}
              >
                {cancelLabel}
              </div>
              <div
                onClick={handleDeleteSale}
                className="cursor-pointer text-[14px] leading-[22px] font-inter font-medium"
                style={{
                  borderRadius: "30px",
                  padding: "10px 24px",
                  backgroundColor: "#BA0E13",
                  color: "#FFFFFF",
                }}
              >
                {deleteLabel}
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default Sales;
